import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/data/site";
import { EASE } from "./motion";

const fmtDetail = (d) => (typeof d === "string" ? d : "Something went wrong");

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadLeads = async () => {
    try {
      const res = await fetch(`${API}/leads`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLeads(data.leads);
    } catch {
      toast.error("Could not load leads");
    }
  };

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        setUser(u);
        if (u) loadLeads();
      })
      .catch(() => setUser(null));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(fmtDetail(data.detail));
      setUser(data);
      loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setLeads([]);
  };

  if (user === undefined) return <div className="flex min-h-screen items-center justify-center bg-[#1B1B1B]"><span className="animate-spin-slow block h-10 w-10 rounded-full border border-[#F7F5F2]/10 border-t-[#B68D40]" /></div>;

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#1B1B1B] px-6">
        <motion.form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-2xl bg-[#F7F5F2] p-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p className="font-display text-2xl text-[#1B1B1B]">Bengaluru <span className="italic text-[#B68D40]">Interiors</span></p>
          <p className="mt-1 font-num text-[10px] uppercase tracking-[0.3em] text-[#2C2C2C]/50">Studio Dashboard</p>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-8 h-12 w-full rounded-lg border border-[#1B1B1B]/15 bg-white px-4 text-sm outline-none focus:border-[#B68D40]"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-4 h-12 w-full rounded-lg border border-[#1B1B1B]/15 bg-white px-4 text-sm outline-none focus:border-[#B68D40]"
          />
          {error && <p data-testid="admin-login-error" className="mt-3 text-xs text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-btn"
            className="mt-6 w-full rounded-full bg-[#1B1B1B] py-4 text-sm font-semibold uppercase tracking-widest text-[#F7F5F2] transition-colors hover:bg-[#B68D40] hover:text-[#1B1B1B] disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2] px-6 py-10 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-display text-3xl text-[#1B1B1B]">Enquiries <span className="italic text-[#B68D40]">({leads.length})</span></p>
            <p className="mt-1 text-xs text-[#2C2C2C]/50">Signed in as {user.email}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={loadLeads} data-testid="admin-refresh-btn" className="flex h-11 w-11 items-center justify-center rounded-full border hairline text-[#1B1B1B] transition-colors hover:bg-[#1B1B1B] hover:text-[#F7F5F2]" aria-label="Refresh">
              <RefreshCw size={16} />
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex h-11 w-11 items-center justify-center rounded-full border hairline text-[#1B1B1B] transition-colors hover:bg-[#1B1B1B] hover:text-[#F7F5F2]" aria-label="Logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border hairline bg-white">
          {leads.length === 0 ? (
            <p data-testid="admin-empty" className="p-16 text-center font-serif2 text-xl italic text-[#2C2C2C]/40">
              No enquiries yet — they'll appear here the moment someone reaches out.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b hairline font-num text-[10px] uppercase tracking-[0.25em] text-[#2C2C2C]/45">
                    <th className="px-6 py-4">When</th>
                    <th className="px-6 py-4">Name</th>
                    <th className="px-6 py-4">Contact</th>
                    <th className="px-6 py-4">Source</th>
                    <th className="px-6 py-4">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id || l.phone} data-testid={`lead-row-${l.id}`} className="border-b hairline last:border-0 hover:bg-[#F7F5F2]/60">
                      <td className="px-6 py-4 font-num text-xs text-[#2C2C2C]/55">
                        {l.created_at ? new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                      </td>
                      <td className="px-6 py-4 font-medium text-[#1B1B1B]">{l.name}</td>
                      <td className="px-6 py-4">
                        <span className="flex items-center gap-1.5 text-xs"><Phone size={12} className="text-[#B68D40]" />{l.phone}</span>
                        {l.email && <span className="mt-1 flex items-center gap-1.5 text-xs text-[#2C2C2C]/55"><Mail size={12} className="text-[#B68D40]" />{l.email}</span>}
                      </td>
                      <td className="px-6 py-4">
                        <span className="rounded-full bg-[#E7DDD0] px-3 py-1 font-num text-[10px] uppercase tracking-widest text-[#1B1B1B]">{l.source}</span>
                      </td>
                      <td className="max-w-[220px] px-6 py-4 text-xs text-[#2C2C2C]/60">{l.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
